import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonInput, IonicModule } from '@ionic/angular';
import { PipeModule } from 'src/app/pipes/pipe.module';
import { ProfileService } from 'src/app/services/profile.service';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { AttendeePicture } from 'src/models/attendeePicture';
import { MyEventServices } from 'src/app/services/my-event.service';
import { LoginService } from 'src/app/services/login.service';
import { ValidationHelper } from 'src/app/helpers/validationHelper';
import { Profile } from 'src/models/profile';
import { Dialog } from '@capacitor/dialog';
import { FileService } from 'src/app/services/files.service';
import { Subscription } from 'rxjs';
import { AppSettings } from 'src/app/app-settings';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, PipeModule]
})
export class ProfileComponent implements OnInit, OnDestroy {
  isEditMode = false;
  public environment = environment;
  profile: Profile;
  @Input() isNewAppUser: boolean = false;
  @Input() profileId: number = 0;
  @Input() familyId: number = 0;
  @Input() isCoordinator: boolean = false;
  @Input() profileAssignmentId: number = 0;

  lstGenders: any[] = [];
  lstProfileTypes: any[] = [];
  lstPriests: any[] = [];
  lstDeaconRanks: any[] = [];
  lstProfileStatuses: any[] = [];
  lstCities: any[] = [];
  lstEducations: any[] = [];
  lstOccupations: any[] = [];
  lstProfessions: any[] = [];
  lstRelationTypes: any[] = [];
  isProfileInfoOpen: boolean = false;
  subscriptions: Subscription[] = [];

  @ViewChild('mobile1Input', { static: true }) mobile1Input!: IonInput;
  @ViewChild('mobile2Input', { static: true }) mobile2Input!: IonInput;

  constructor(private profileService: ProfileService, private myEventService: MyEventServices, public userService: LoginService,
    private fileService: FileService, private appSettings: AppSettings) { }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  ngOnInit() {
    this.subscriptions.push(this.profileService.getProfileDataTypes().subscribe(allTypes => {
      if (allTypes && allTypes.length > 0) {
        this.lstGenders = allTypes.filter(x => x.TableName === 'Genders').map(x => ({
          label: x.Value,
          value: x.Key
        }));

        this.lstProfileStatuses = allTypes.filter(x => x.TableName === 'AssignmentStatuses').map(x => ({
          label: x.Value,
          value: x.Key
        }));

        this.lstCities = allTypes.filter(x => x.TableName === 'Cities').map(x => ({
          label: x.Value,
          value: x.Key
        }));

        this.lstDeaconRanks = allTypes.filter(x => x.TableName === 'DeaconRanks').map(x => ({
          label: x.Value,
          value: x.Key
        }));

        this.lstEducations = allTypes.filter(x => x.TableName === 'EducationLevels').map(x => ({
          label: x.Value,
          value: x.Key
        }));

        this.lstOccupations = allTypes.filter(x => x.TableName === 'Occupations').map(x => ({
          label: x.Value,
          value: x.Key
        }));

        this.lstPriests = allTypes.filter(x => x.TableName === 'Priests').map(x => ({
          label: x.Value,
          value: x.Key
        }));

        this.lstProfessions = allTypes.filter(x => x.TableName === 'Professions').map(x => ({
          label: x.Value,
          value: x.Key
        }));

        this.lstProfileTypes = allTypes.filter(x => x.TableName === 'ProfileTypes').map(x => ({
          label: x.Value,
          value: x.Key
        }));

        this.lstRelationTypes = allTypes.filter(x => x.TableName === 'RelationTypes').map(x => ({
          label: x.Value,
          value: x.Key
        }));
      }
    }));

    if (this.profileId > 0) {
      this.subscriptions.push(this.profileService.getProfileById(this.profileId, 'Profiles').subscribe(result => {
        this.profile = result;
        this.profileService.familyIdEvent.next(this.profile.FamilyId);
      }));
    }
    else {
      this.isEditMode = true;
      this.profile = {} as Profile;
      this.profile.ProfileId = 0;
      this.profile.FamilyId = this.familyId;
      this.profile.ServiceId = 0;
      this.profile.MarriageDate = null;
      this.profile.BirthDate = null;

      this.subscriptions.push(this.profileService.getFamilyById(this.familyId).subscribe(result => {
        if (result && result.FamilyId && result.FamilyId > 0) {
          this.profile.FullAddress = result.FullAddress;
          this.profile.StreetName = result.StreetName;
          this.profile.CityId = result.CityId;
          this.profile.ZipCode = result.ZipCode.toString();
        }
      }));
    }
  }

  onMobile1Input(ev: any) {
    const value = ev.target!.value;
    const filteredValue = value.replace(/[^0-9]+/g, '');
    this.mobile1Input.value = this.profile.Mobile1 = filteredValue;
  }

  onMobile2Input(ev: any) {
    const value = ev.target!.value;
    const filteredValue = value.replace(/[^0-9]+/g, '');
    this.mobile2Input.value = this.profile.Mobile2 = filteredValue;
  }

  setDateToValue(value: any) {
    if (value) {
      this.profile.MarriageDate = new Date(value);
    }
  }

  saveProfile() {
    if (this.isProfileValid()) {
      //this.myEventService.loadingEvent.emit(true);

      let userId = 0;

      if (this.userService.currentUser && this.userService.currentUser.UserId > 0) {
        userId = this.userService.currentUser.UserId;
      }

      this.profile.ProfileAssignmentId = this.profileAssignmentId;

      this.subscriptions.push(this.profileService.saveProfile(this.profile, userId, this.profile.ServiceId).subscribe(result => {
        if (result) {
          this.isEditMode = false;

          this.myEventService.showToast("success", "Saved", "Profile has been saved");
          this.profile = result;

          if (this.isNewAppUser && this.isNewAppUser == true) {
            this.profileService.closeProfileEvent.emit(this.profile?.ProfileId);
          }
          else {
            this.profileService.profileUpdated.emit(this.profile?.ProfileId);
          }
        }
        else {
          this.myEventService.showToast("error", "Failed to save", "There was an error saving profile, please try again later");
        }

        this.myEventService.loadingEvent.emit(false);
      }));
    }
  }

  isProfileValid() {
    if (!this.profile.FirstName || this.profile.FirstName.length < 1) {
      this.myEventService.showToast("error", "First Name", "First name cannot be empty");
      return false;
    }
    else if (!this.profile.LastName || this.profile.LastName.length < 1) {
      this.myEventService.showToast("error", "Last Name", "Last name cannot be empty");
      return false;
    }
    else if (!this.profile.BirthDate || this.profile.BirthDate.toLocaleString().length < 1) {
      this.myEventService.showToast("error", "Birth Date", "Birth Date cannot be empty");
      return false;
    }
    else if (!this.profile.GenderId || this.profile.GenderId < 1) {
      this.myEventService.showToast("error", "Gender", "Gender cannot be empty");
      return false;
    }
    else if (!this.profile.RelationTypeId || this.profile.RelationTypeId < 1) {
      this.myEventService.showToast("error", "Relation Type", "Relation Type is required");
      return false;
    }
    else if (!this.profile.Mobile1 || this.profile.Mobile1.length < 10) {
      this.myEventService.showToast("error", "Mobile 1", "Mobile 1 needs to be at least 10 characeters including area code");
      return false;
    }
    else if (this.profile.Mobile2 && this.profile.Mobile2.length > 0 && this.profile.Mobile2.length < 10) {
      this.myEventService.showToast("error", "Mobile 2", "Mobile 2 needs to be at least 10 characeters including area code");
      return false;
    }
    else if (ValidationHelper.ValidateEmail(this.profile.PrimaryEmail).length > 0) {
      this.myEventService.showToast("error", "Primary Email", ValidationHelper.ValidateEmail(this.profile.PrimaryEmail));
      return false;
    }

    // if a member is joining a family, don't validate the address
    if (!this.profile.FamilyId || this.profile.FamilyId == 0) {
      if (!this.profile.StreetName || this.profile.StreetName.length < 4) {
        this.myEventService.showToast("error", "Address", "Address is requried");
        return false;
      }
      if (!this.profile.CityId || this.profile.CityId < 1) {
        this.myEventService.showToast("error", "City", "City is requried");
        return false;
      }
      if (!this.profile.ZipCode || this.profile.ZipCode.length < 1) {
        this.myEventService.showToast("error", "Zipcode", "Zipcode is requried");
        return false;
      }
    }

    return true;
  }

  closeProfile() {
    this.profileService.closeProfileEvent.emit(0);
  }

  async deleteAccount() {
    const { value } = await Dialog.confirm({
      title: 'Delete Account?',
      message: `Are you sure you want to delete your account, no turning back?`,
    });

    if (value == true) {
      let userId = this.userService.currentUser?.UserId;

      //this.myEventService.loadingEvent.emit(true);
      this.userService.deleteAccount(userId).subscribe(result => {
        if (result && result == true) {
          this.myEventService.loadingEvent.emit(false);
          this.userService.logout();
          this.myEventService.showToast("success", "Account Deleted", "Your account has been deleted.");
        }
        else {
          this.myEventService.showToast("error", "Action Failed", "Unable to delete your account at this time.");
        }
      });
    }
  }

  openMaps(location: string) {
    this.appSettings.openMaps(location);
  }

  async openCamera() {
    const image = await Camera.getPhoto({
      quality: 100,
      resultType: CameraResultType.Base64,
      source: CameraSource.Prompt
    });

    if (image) {
      this.savePicture(image);
    }
  }

  savePicture(imageData: Photo) {
    this.profile.ImageSrc = "data:image/jpg;base64," + imageData.base64String;

    let attendeePicture: AttendeePicture = {
      ProfileId: this.profile.ProfileId,
      PhotoBase64: imageData.base64String,
      EventId: 0
    };

    this.profileService.savePicture(attendeePicture).subscribe(result => {
      if (result && result == true) {
        this.myEventService.showToast("success", "Picture Saved", "Picture saved successfully");
      }
      else {
        this.myEventService.showToast("error", "Picture Failed", "Failed to save picture");
      }
    });
  }

  downloadvCard() {
    this.profileService.getContactCard(this.profile.ProfileId).subscribe(vCard => {
      if (vCard) {
        this.fileService.exportContactCards(vCard, this.profile.ProfileName);
      }
      else {
        this.myEventService.showToast("error", "Action Failed", "Unable to get contact card, please report this issue");
      }
    });
  }
}